import { ApiError } from '@/utils/errors';

const API_URL = import.meta.env.VITE_API_URL || '';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // 1s, 2s, 4s

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  retries?: number;
}

/**
 * API response format from Worker
 */
interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
  };
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

/**
 * Check if a status code is retryable
 */
function isRetryable(status: number): boolean {
  return status >= 500 || status === 429;
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Base API client with retry logic and session management
 */
export class BaseApiClient {
  private sessionToken: string | null = null;
  private storeHash: string | null = null;

  setSession(sessionToken: string, storeHash: string) {
    this.sessionToken = sessionToken;
    this.storeHash = storeHash;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, retries = MAX_RETRIES } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    if (this.storeHash) {
      headers['X-Store-Hash'] = this.storeHash;
    }

    let lastError: Error | null = null;
    let attempt = 0;

    while (attempt <= retries) {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
          const errorData = await this.parseErrorResponse(response);

          if (isRetryable(response.status) && attempt < retries) {
            lastError = errorData;
            attempt++;
            await sleep(RETRY_DELAYS[attempt - 1] || 4000);
            continue;
          }

          throw errorData;
        }

        const json = await response.json();

        if (this.isWrappedResponse(json)) {
          if (json.success) {
            return json.data as T;
          } else {
            throw new ApiError(
              response.status,
              json.error.code,
              json.error.message,
              json.error.details,
              json.error.requestId
            );
          }
        }

        return json as T;

      } catch (err) {
        if (err instanceof ApiError) {
          throw err;
        }

        if (attempt < retries) {
          lastError = err instanceof Error ? err : new Error(String(err));
          attempt++;
          await sleep(RETRY_DELAYS[attempt - 1] || 4000);
          continue;
        }

        throw err;
      }
    }

    throw lastError || new Error('Request failed after retries');
  }

  private async parseErrorResponse(response: Response): Promise<ApiError> {
    try {
      const json = await response.json();

      if (json && typeof json === 'object' && 'success' in json && !json.success) {
        const errorResponse = json as ApiErrorResponse;
        return new ApiError(
          response.status,
          errorResponse.error.code,
          errorResponse.error.message,
          errorResponse.error.details,
          errorResponse.error.requestId
        );
      }

      if (json && typeof json === 'object') {
        const legacyError = json as { error?: string; message?: string; code?: string };
        return new ApiError(
          response.status,
          legacyError.code || 'UNKNOWN_ERROR',
          legacyError.error || legacyError.message || `Request failed: ${response.status}`
        );
      }

      return new ApiError(
        response.status,
        'UNKNOWN_ERROR',
        JSON.stringify(json) || `Request failed: ${response.status}`
      );

    } catch {
      try {
        const text = await response.text();
        return new ApiError(
          response.status,
          'UNKNOWN_ERROR',
          text || `Request failed: ${response.status}`
        );
      } catch {
        return new ApiError(
          response.status,
          'UNKNOWN_ERROR',
          `Request failed: ${response.status}`
        );
      }
    }
  }

  private isWrappedResponse(json: unknown): json is ApiResponse<unknown> {
    return (
      json !== null &&
      typeof json === 'object' &&
      'success' in json &&
      typeof (json as { success: unknown }).success === 'boolean'
    );
  }

  // Convenience methods
  protected get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  protected post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body });
  }

  protected put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body });
  }

  protected delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

// Singleton instance
export const apiClient = new BaseApiClient();
